import { supabase } from '../lib/supabase'

export async function fetchPlayers() {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .order('display_name')

  if (error) throw error
  return data
}

export async function createPlayer({ first_name, last_name, display_name, email, avatar_color, user_id: linkedUserId, ghin_number, handicap_index, home_course, gender }) {
  const { data: { user } } = await supabase.auth.getUser()

  // If a user_id was explicitly provided (from search), use it directly.
  // Otherwise auto-link if the email matches the current user's email.
  const userId = linkedUserId
    ? linkedUserId
    : (email && user.email && email.toLowerCase() === user.email.toLowerCase())
      ? user.id
      : null

  const { data, error } = await supabase
    .from('players')
    .insert({
      first_name: first_name || null,
      last_name: last_name || null,
      display_name,
      email: email || null,
      avatar_color,
      created_by: user.id,
      user_id: userId,
      ...(ghin_number != null ? { ghin_number } : {}),
      ...(handicap_index != null ? { handicap_index } : {}),
      ...(home_course != null ? { home_course } : {}),
      ...(gender ? { gender } : {}),
    })
    .select()
    .single()

  if (error) throw error

  // Auto-follow manually created players (skip if creating own record)
  if (data && !userId) {
    const { data: myPlayer } = await supabase
      .from('players')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (myPlayer) {
      // Creator follows the new player
      await supabase
        .from('follows')
        .insert({ follower_id: myPlayer.id, following_id: data.id })
        .select()
        .maybeSingle()
      // New player follows the creator back
      await supabase
        .from('follows')
        .insert({ follower_id: data.id, following_id: myPlayer.id })
        .select()
        .maybeSingle()
    }
  }

  return data
}

export async function findPlayerByUserId(userId) {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) throw error
  return data
}

/**
 * Link a player to the current auth user (call when user claims a player profile).
 */
export async function linkPlayerToUser(playerId) {
  const { data: { user } } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('players')
    .update({ user_id: user.id })
    .eq('id', playerId)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function updatePlayer(id, updates) {
  const { data, error } = await supabase
    .from('players')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deletePlayer(id) {
  const { error } = await supabase
    .from('players')
    .delete()
    .eq('id', id)

  if (error) throw error
}

export async function fetchPlayer(playerId) {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('id', playerId)
    .single()

  if (error) throw error
  return data
}

export async function fetchMyPlayer() {
  const { data: { user } } = await supabase.auth.getUser()
  // Throw instead of returning null — if the query is enabled (user exists in
  // React state) but getUser() returns null, the auth token is being refreshed.
  // Throwing lets React Query retry with backoff and keeps the old cached data
  // visible, preventing profile fields from being wiped during reconnect.
  if (!user) throw new Error('Auth session not available')

  const { data, error } = await supabase
    .from('players')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data
}

export async function updatePlayerHandicap(playerId, handicapIndex) {
  const { error } = await supabase.rpc('update_player_handicap', {
    p_player_id: playerId,
    p_handicap_index: handicapIndex,
    p_handicap_updated_at: new Date().toISOString(),
  })
  if (error) throw error
}

export async function lookupGhinHandicap(ghinNumber) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const { data, error } = await supabase.functions.invoke('ghin-lookup', {
    body: { ghin_number: ghinNumber },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (error) throw error
  if (data?.error) throw new Error(data.error)
  return data
}

/**
 * Batch lookup GHIN handicaps for multiple players.
 * Accepts array of { playerId, ghinNumber } objects.
 * Returns map of ghinNumber -> result.
 */
export async function batchLookupGhinHandicaps(ghinNumbers) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const { data, error } = await supabase.functions.invoke('ghin-lookup', {
    body: { ghin_numbers: ghinNumbers },
    headers: { Authorization: `Bearer ${session.access_token}` },
  })

  if (error) throw error
  if (data?.error) throw new Error(data.error)
  return data.results
}
